import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Table, Thead, Tbody, Tr, Th, Td, Button } from "@chakra-ui/react";
import { FaSolarPanel, FaBatteryFull, FaChartLine, FaCheckCircle, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const SolutionsInvestmentSolarBattery: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solutions/investment-solar-battery"
        fallbackTitle="Investment Solar + Battery Solutions"
        fallbackDescription="Strategic battery storage combined with investment property financing"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="2xl" fontWeight="bold">
              Investment Property Solar + Battery Upgrade
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Maximize rental returns with solar and battery storage
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Investment Metrics Showcase (4 big numbers) */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
          <Box bg={brandConfig.colors.primary} p={8} borderRadius="xl" color="white" textAlign="center" boxShadow="xl">
            <Stat>
              <StatLabel fontSize="sm" mb={2} opacity={0.9}>Annual ROI</StatLabel>
              <StatNumber fontSize="5xl" fontWeight="bold">18-25%</StatNumber>
              <StatHelpText fontSize="xs" opacity={0.8} mt={2}>Combined solar + battery return</StatHelpText>
            </Stat>
          </Box>

          <Box bg={brandConfig.colors.primary} p={8} borderRadius="xl" color="white" textAlign="center" boxShadow="xl">
            <Stat>
              <StatLabel fontSize="sm" mb={2} opacity={0.9}>Payback Period</StatLabel>
              <StatNumber fontSize="5xl" fontWeight="bold">4-6</StatNumber>
              <StatHelpText fontSize="xs" opacity={0.8} mt={2}>Years to full investment return</StatHelpText>
            </Stat>
          </Box>

          <Box bg={brandConfig.colors.primary} p={8} borderRadius="xl" color="white" textAlign="center" boxShadow="xl">
            <Stat>
              <StatLabel fontSize="sm" mb={2} opacity={0.9}>Property Value Increase</StatLabel>
              <StatNumber fontSize="5xl" fontWeight="bold">$30k+</StatNumber>
              <StatHelpText fontSize="xs" opacity={0.8} mt={2}>Immediate equity boost</StatHelpText>
            </Stat>
          </Box>

          <Box bg="#C2A878" p={8} borderRadius="xl" color={brandConfig.colors.primary} textAlign="center" boxShadow="xl">
            <Stat>
              <StatLabel fontSize="sm" mb={2} opacity={0.9}>Rental Premium</StatLabel>
              <StatNumber fontSize="5xl" fontWeight="bold">$50-80</StatNumber>
              <StatHelpText fontSize="xs" opacity={0.8} mt={2}>Extra weekly rent achievable</StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Battery vs No Battery Comparison Table */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={12} textAlign="center">
            Solar Only vs Solar + Battery Investment
          </Heading>
          <Box bg="white" borderRadius="xl" overflow="hidden" boxShadow="lg">
            <Table variant="simple">
              <Thead bg="gray.100">
                <Tr>
                  <Th fontSize="md" py={4}>Feature</Th>
                  <Th fontSize="md" py={4} textAlign="center">Solar Only</Th>
                  <Th fontSize="md" py={4} textAlign="center" bg={brandConfig.colors.primary} color="white">Solar + Battery</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td fontWeight="medium">Initial Investment</Td>
                  <Td textAlign="center">$8,000 - $12,000</Td>
                  <Td textAlign="center" bg="gray.50">$15,000 - $22,000</Td>
                </Tr>
                <Tr bg="gray.50">
                  <Td fontWeight="medium">Annual Savings</Td>
                  <Td textAlign="center">$1,500 - $2,000</Td>
                  <Td textAlign="center" bg="gray.100" fontWeight="bold" color={brandConfig.colors.primary}>$3,000 - $4,500</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="medium">Energy Independence</Td>
                  <Td textAlign="center"><Box as={FaTimes} color="gray.400" display="inline" /> Day only</Td>
                  <Td textAlign="center" bg="gray.50"><Box as={FaCheckCircle} color={brandConfig.colors.primary} display="inline" /> 24/7 coverage</Td>
                </Tr>
                <Tr bg="gray.50">
                  <Td fontWeight="medium">Peak Rate Avoidance</Td>
                  <Td textAlign="center"><Box as={FaTimes} color="gray.400" display="inline" /> Limited</Td>
                  <Td textAlign="center" bg="gray.100"><Box as={FaCheckCircle} color={brandConfig.colors.primary} display="inline" /> Full protection</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="medium">Blackout Protection</Td>
                  <Td textAlign="center"><Box as={FaTimes} color="gray.400" display="inline" /> No</Td>
                  <Td textAlign="center" bg="gray.50"><Box as={FaCheckCircle} color={brandConfig.colors.primary} display="inline" /> Yes - backup power</Td>
                </Tr>
                <Tr bg="gray.50">
                  <Td fontWeight="medium">Tenant Appeal</Td>
                  <Td textAlign="center">High</Td>
                  <Td textAlign="center" bg="gray.100" fontWeight="bold" color={brandConfig.colors.primary}>Premium</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="medium">Rental Premium Potential</Td>
                  <Td textAlign="center">$30-40/week</Td>
                  <Td textAlign="center" bg="gray.50" fontWeight="bold" color={brandConfig.colors.primary}>$50-80/week</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </Container>
      </Box>

      {/* Technology Features (3 cards) */}
      <Container maxW="container.xl" py={16}>
        <Heading size="lg" mb={12} textAlign="center">
          Complete Energy System for Your Investment
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          <VStack bg="white" p={8} borderRadius="xl" border="2px solid" borderColor="gray.200" spacing={4} align="start" _hover={{ borderColor: brandConfig.colors.primary, boxShadow: "xl" }} transition="all 0.3s">
            <Box bg="rgba(20, 28, 73, 0.08)" p={4} borderRadius="full">
              <Box as={FaSolarPanel} size="32px" color={brandConfig.colors.primary} />
            </Box>
            <Heading size="md">Premium Solar Panels</Heading>
            <Text color="gray.600">
              High-efficiency panels with 25-year warranty, designed for maximum energy generation in Australian conditions.
            </Text>
            <VStack align="start" spacing={2} w="100%">
              <Text fontSize="sm" fontWeight="bold" color="gray.700">Specifications:</Text>
              <Text fontSize="sm" color="gray.600">• 6-10kW system capacity</Text>
              <Text fontSize="sm" color="gray.600">• Tier 1 monocrystalline panels</Text>
              <Text fontSize="sm" color="gray.600">• 20-22% efficiency rating</Text>
              <Text fontSize="sm" color="gray.600">• Weather-resistant design</Text>
            </VStack>
          </VStack>

          <VStack bg="white" p={8} borderRadius="xl" border="2px solid" borderColor="#C2A878" spacing={4} align="start" boxShadow="lg">
            <Box bg="#C2A878" p={4} borderRadius="full">
              <Box as={FaBatteryFull} size="32px" color={brandConfig.colors.primary} />
            </Box>
            <Heading size="md">Smart Battery Storage</Heading>
            <Text color="gray.600">
              Store excess solar energy for use during peak times and at night, maximizing savings and providing backup power.
            </Text>
            <VStack align="start" spacing={2} w="100%">
              <Text fontSize="sm" fontWeight="bold" color="gray.700">Specifications:</Text>
              <Text fontSize="sm" color="gray.600">• 10-13.5kWh storage capacity</Text>
              <Text fontSize="sm" color="gray.600">• 10-year performance warranty</Text>
              <Text fontSize="sm" color="gray.600">• Smart grid integration</Text>
              <Text fontSize="sm" color="gray.600">• Blackout backup capability</Text>
            </VStack>
          </VStack>

          <VStack bg="white" p={8} borderRadius="xl" border="2px solid" borderColor="gray.200" spacing={4} align="start" _hover={{ borderColor: brandConfig.colors.primary, boxShadow: "xl" }} transition="all 0.3s">
            <Box bg="rgba(20, 28, 73, 0.08)" p={4} borderRadius="full">
              <Box as={FaChartLine} size="32px" color={brandConfig.colors.primary} />
            </Box>
            <Heading size="md">Real-Time Monitoring</Heading>
            <Text color="gray.600">
              Track energy production, consumption, and savings through an intuitive app - perfect for landlords and tenants.
            </Text>
            <VStack align="start" spacing={2} w="100%">
              <Text fontSize="sm" fontWeight="bold" color="gray.700">Features:</Text>
              <Text fontSize="sm" color="gray.600">• Live production dashboard</Text>
              <Text fontSize="sm" color="gray.600">• Historical data analysis</Text>
              <Text fontSize="sm" color="gray.600">• Cost savings reports</Text>
              <Text fontSize="sm" color="gray.600">• Performance alerts</Text>
            </VStack>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* CTA Section */}
      <Box bg={brandConfig.colors.primary} py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center" color="white">
            <Heading size="xl">Upgrade Your Investment Property Today</Heading>
            <Text fontSize="lg" maxW="3xl" opacity={0.9}>
              Get a custom solar + battery proposal for your investment property. See exactly how much you'll save and how quickly you'll recoup your investment.
            </Text>
            <HStack spacing={4}>
              <Button size="lg" bg="white" color={brandConfig.colors.primary} _hover={{ bg: "gray.100" }} onClick={() => navigate("/contact")}>
                Get Custom Proposal
              </Button>
              <Button size="lg" variant="outline" colorScheme="whiteAlpha" borderColor="white" color="white" _hover={{ bg: "whiteAlpha.200" }}>
                View Case Studies
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolutionsInvestmentSolarBattery;
